import { Dispatch, memo, SetStateAction, useMemo, useState } from "react"
import { Box } from "@mui/material"
import { AxiosInstance } from "axios"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../../modules/localization/components/AppLocalized"
import { ApiQueryAutocomplete } from "../../../../core/api/components/ApiQueryAutocomplete"

import {
    CompanyAdminUsersQuery,
    fetchCompaniesUsers,
} from "../../../../modules/company/admin/organization/users/usersApi"
import { UserType } from "../../../../modules/login/loginTypes"
import { useApiQuery } from "../../../../core/api/useApiQuery"
import { UserDetailsProps } from "./OrderOnBehalfFlow"
import {
    CreateOrderOnBehalfOfQuery,
    fetchOrderOnBehalfOfUsersForMerchant,
    fetchOrderOnBehalfOfUsersForServiceCenter,
    OrderOnBehalfOfUsersForMerchantResponse,
} from "./orderOnBehalfFlowApi"
import { TranslationKey } from "../../../../modules/localization/translations"
import { useEnvironment } from "../../../../modules/environment/envHooks"
import { EnvironmentType } from "../../../../modules/environment/envTypes"
import { useAppLocalization } from "../../../../modules/localization/components/AppLocalizationProvider"

const useStyles = makeStyles()((_theme) => ({
    input: {
        "& .MuiFormControl-root": {
            width: "100%",
        },
    },
}))

const transformData = (data: OrderOnBehalfOfUsersForMerchantResponse) => data?.users || []

type UserSelectorProps = Readonly<{
    id: number
    userDetails: UserDetailsProps
    userType: UserType.MERCHANT | UserType.SERVICE_CENTER | UserType.COMPANY
    setUserDetails: Dispatch<SetStateAction<UserDetailsProps>>
    labelTranslationKey?: TranslationKey
}>

export const UserSelector = memo(
    ({
        id,
        userDetails,
        userType,
        setUserDetails,
        labelTranslationKey = "create-order-on-behalf-of-dialog-select-user-field",
    }: UserSelectorProps) => {
        const { l10n } = useAppLocalization()
        const { classes } = useStyles()
        const { currentEnv } = useEnvironment()

        const [searchText, setSearchText] = useState<string>("")

        const { queryFn, queryName } = useMemo(() => {
            return {
                queryFn: async (
                    {
                        id,
                        type,
                        companyId,
                        companyGroupId,
                        search,
                    }: { id: number; type: UserType; companyId?: string; companyGroupId?: string; search?: string },
                    axios: AxiosInstance,
                    signal?: AbortSignal,
                ) => {
                    // FIXME: HACK: Grab from merchant instead for these service centers
                    if (currentEnv.type === EnvironmentType.Test && type === UserType.SERVICE_CENTER && id === 2776) {
                        type = UserType.MERCHANT
                        id = 2
                    } else if (
                        (currentEnv.type === EnvironmentType.QA || currentEnv.type === EnvironmentType.Prod) &&
                        type === UserType.SERVICE_CENTER &&
                        id === 176
                    ) {
                        type = UserType.MERCHANT
                        const id = 4108
                    }

                    if (type === UserType.MERCHANT) {
                        const params: {
                            merchantId: number
                            companyId?: string
                            userGroupId?: string
                            searchText?: string
                        } = {
                            merchantId: id,
                        }

                        if (companyId) params.companyId = companyId
                        if (companyGroupId) params.userGroupId = companyGroupId
                        if (search) params.searchText = search

                        return fetchOrderOnBehalfOfUsersForMerchant(params, axios, signal)
                    }

                    if (type === UserType.SERVICE_CENTER) {
                        const params: {
                            serviceCenterId: number
                            companyId?: string
                            userGroupId?: string
                            searchText?: string
                        } = {
                            serviceCenterId: id,
                            userGroupId: companyGroupId,
                            companyId: companyId,
                        }

                        if (search) params.searchText = search

                        return fetchOrderOnBehalfOfUsersForServiceCenter(params, axios, signal)
                    }

                    return fetchCompaniesUsers({ companyId: id, search }, axios, signal)
                },
                queryName:
                    userType === UserType.MERCHANT
                        ? CreateOrderOnBehalfOfQuery.USERS_MERCHANT
                        : userType === UserType.SERVICE_CENTER
                          ? CreateOrderOnBehalfOfQuery.COMPANIES_USERS_SERVICE_CENTER
                          : CompanyAdminUsersQuery.USERS_FOR_COMPANY,
            }
        }, [userType, userDetails.companyId, userDetails.companyGroupId, currentEnv.type])

        const {
            data: result = [],
            isError,
            isLoading,
        } = useApiQuery(queryFn, {
            queryName,
            dependencies: {
                id,
                search: searchText,
                type: userType,
                companyId: userDetails.companyId,
                companyGroupId: userDetails.companyGroupId,
            },
            select: transformData,
            enabled: searchText.length >= 3,
        })

        const selectedValue = useMemo(() => {
            if (!userDetails.selectedUserId || result?.length === 0) return undefined

            return (
                result.find((user) => {
                    return user.id === userDetails.selectedUserId
                }) || undefined
            )
        }, [result, userDetails.selectedUserId])

        const autocompleteKey = useMemo(() => {
            return `${userDetails.companyId || "none"}-${userDetails.companyGroupId || "none"}`
        }, [userDetails.companyId, userDetails.companyGroupId])

        return (
            <Box className={classes.input}>
                <LocalizedStrict id={labelTranslationKey} attrs={{ label: true, placeholder: true }}>
                    <ApiQueryAutocomplete
                        key={autocompleteKey}
                        isLoading={isLoading && searchText.length >= 3}
                        isError={isError}
                        options={result}
                        value={selectedValue}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        getOptionLabel={(option) => option.displayName}
                        onChange={(_, value) =>
                            setUserDetails((prev) => {
                                return {
                                    ...prev,
                                    selectedUserId: value?.id,
                                    selectedUserName: value?.displayName,
                                }
                            })
                        }
                        onInputChange={(_, newInputValue, reason) => {
                            if (reason === "input") {
                                setSearchText(newInputValue)
                            }
                        }}
                        label="Select user"
                        placeholder="Enter user name:"
                        noOptionsText={
                            searchText.length >= 3
                                ? l10n.getString("create-order-on-behalf-of-dialog-select-user-field-no-results")
                                : ""
                        }
                        slotProps={{
                            textField: { className: classes.input },
                            errorSelect: { className: classes.input },
                        }}
                    />
                </LocalizedStrict>
            </Box>
        )
    },
)
